import { useEffect, useState } from 'react';
import { Search, Download } from 'lucide-react';
import { useLang } from '../../../i18n';
import { get, post } from '../../../services/api';
import Card from '../../../components/Card';
import Input from '../../../components/Input';
import Button from '../../../components/Button';
import EmptyState from '../../../components/EmptyState';
import Skeleton from '../../../components/Skeleton';
import type { Paginated } from '../../../types';

interface OrderFile {
  public_id: string;
  name: string;
  size: number;
  uploaded_by_name?: string;
  created_at: string;
  category?: string;
}

interface Props { publicId: string }

function fmtSize(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

export default function OrderFiles({ publicId }: Props) {
  const { t } = useLang();
  const [items, setItems] = useState<OrderFile[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await get<Paginated<OrderFile>>(`/me/orders/${publicId}/files/`);
        if (!cancelled) setItems(res.results || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [publicId]);

  const onDownload = async (id: string) => {
    try {
      const res = await post<{ url: string }>(`/me/documents/${id}/download/`);
      if (res?.url) window.open(res.url, '_blank', 'noopener');
    } catch {
      void 0;
    }
  };

  const filtered = items.filter((f) => !query || f.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="order-files">
      <Card padding="md">
        <Input
          placeholder={t('order.files.search')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          iconLeft={<Search size={16} />}
          aria-label={t('order.files.search')}
        />
      </Card>
      {loading ? (
        <Skeleton height={100} />
      ) : filtered.length === 0 ? (
        <Card padding="lg"><EmptyState title={t('common.error')} /></Card>
      ) : (
        <Card padding="none">
          <ul className="order-files__list">
            {filtered.map((f, i) => (
              <li key={f.public_id} className="order-files__row">
                {i > 0 ? <hr className="order-files__divider" /> : null}
                <div className="order-files__row-inner">
                  <div className="order-files__info">
                    <div className="order-files__name">{f.name}</div>
                    <div className="order-files__meta">
                      <span>{fmtSize(f.size)}</span>
                      <span>•</span>
                      <span>{new Date(f.created_at).toLocaleDateString()}</span>
                      {f.uploaded_by_name ? <><span>•</span><span>{f.uploaded_by_name}</span></> : null}
                    </div>
                  </div>
                  <Button variant="secondary" size="sm" iconLeft={<Download size={14} />} onClick={() => onDownload(f.public_id)}>
                    {t('order.files.download')}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
